import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import LeadFilterBar, { defaultFilters, filtersToParams } from "../components/LeadFilterBar";
import { toast } from "sonner";
import { 
  Search, 
  Plus, 
  Phone, 
  MapPin, 
  Building2,
  Filter,
  LayoutGrid,
  List,
  Download,
  Upload,
  UserPlus,
  Loader2,
  X,
  Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  prospect: "status-prospect",
  contacted: "status-contacted",
  qualified: "status-qualified",
  closed_won: "status-closed-won",
  closed_lost: "status-closed-lost"
};

const statusLabels = {
  prospect: "Prospect",
  contacted: "Contacted",
  qualified: "Qualified",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost"
};

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const ASSIGNABLE_ROLES = ["owner", "admin", "sales_manager"];
const ALL_ROLES = ["owner", "admin", "sales_manager", "sales_rep"];

export default function LeadsPage({ user }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filters, setFilters] = useState(defaultFilters());
  const [viewMode, setViewMode] = useState("list");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [newLead, setNewLead] = useState({
    business_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact2_name: "",
    contact2_email: "",
    contact2_phone: "",
    address: "",
    city: "",
    state: "CA",
    zip_code: "",
    phone: "",
    email: "",
    website: "",
    category: "",
    notes: "",
    assigned_to: ""
  });

  // Multi-select state
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignToUserId, setAssignToUserId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Current authenticated user (for platform_owner gating + role checks)
  const [currentUser, setCurrentUser] = useState(user || null);
  const effectiveUser = user || currentUser;
  const isPlatformOwner = effectiveUser?.system_role === "platform_owner";
  const canAssign = ASSIGNABLE_ROLES.includes(effectiveUser?.role);
  const canMultiSelect = ALL_ROLES.includes(effectiveUser?.role);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const statusFromUrl = searchParams.get("status");
    if (statusFromUrl) {
      setStatusFilter(statusFromUrl);
      setFilters((f) => ({ ...f, statuses: [statusFromUrl] }));
    }
    fetchLeads();
    fetchUsers();
    if (!user) fetchCurrentUser();
  }, [searchParams]);

  useEffect(() => {
    fetchLeads();
  }, [statusFilter, searchQuery, filters]);

  const fetchLeads = async () => {
    try {
      const params = filtersToParams(filters);
      // Back-compat: also honor the legacy single-status Select if filters has none
      if ((!filters.statuses || filters.statuses.length === 0) && statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "100");

      const response = await fetch(`${API}/leads?${params}`, {
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error("Failed to fetch current user:", error);
    }
  };

  const handleCreateLead = async () => {
    if (!newLead.business_name || !newLead.city || !newLead.state) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const response = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newLead)
      });

      if (response.ok) {
        toast.success("Lead created successfully");
        setIsAddDialogOpen(false);
        setNewLead({
          business_name: "",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          contact2_name: "",
          contact2_email: "",
          contact2_phone: "",
          address: "",
          city: "",
          state: "",
          zip_code: "",
          phone: "",
          email: "",
          website: "",
          category: "",
          notes: "",
          assigned_to: ""
        });
        fetchLeads();
      } else {
        toast.error("Failed to create lead");
      }
    } catch (error) {
      toast.error("Failed to create lead");
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch(`${API}/leads/export-csv`, {
        credentials: "include"
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leads_export.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Leads exported successfully");
      } else {
        toast.error("Failed to export leads");
      }
    } catch (error) {
      toast.error("Failed to export leads");
    }
  };

  const handleExportAllCSV = async () => {
    try {
      toast.info("Preparing full export — this may take a moment...");
      const response = await fetch(`${API}/leads/export-csv-all`, {
        credentials: "include"
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leads_export_all.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Full lead export downloaded (all orgs, all sources)");
      } else if (response.status === 403) {
        toast.error("Only the Platform Owner can run this export");
      } else {
        toast.error("Failed to export all leads");
      }
    } catch (error) {
      toast.error("Failed to export all leads");
    }
  };

  // Multi-select handlers
  const toggleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeads.size === leads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map(l => l.lead_id)));
    }
  };

  const clearSelection = () => {
    setSelectedLeads(new Set());
  };

  const handleBulkAssign = async () => {
    if (!assignToUserId || selectedLeads.size === 0) {
      toast.error("Select leads and a team member");
      return;
    }

    setAssigning(true);
    try {
      const response = await fetch(`${API}/manager/bulk-reassign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lead_ids: Array.from(selectedLeads),
          new_rep_id: assignToUserId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Assigned ${data.count} lead${data.count !== 1 ? "s" : ""} to ${data.new_rep_name}`);
        setSelectedLeads(new Set());
        setAssignDialogOpen(false);
        setAssignToUserId("");
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to assign leads");
      }
    } catch (error) {
      toast.error("Failed to assign leads");
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedLeads.size === 0) return;
    try {
      const response = await fetch(`${API}/leads/export-csv-selected`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_ids: Array.from(selectedLeads) }),
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leads_export_selected.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${selectedLeads.size} lead${selectedLeads.size !== 1 ? "s" : ""}`);
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || "Failed to export selected leads");
      }
    } catch (error) {
      toast.error("Failed to export selected leads");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.size === 0) return;
    if (!window.confirm(`Delete ${selectedLeads.size} selected lead${selectedLeads.size !== 1 ? "s" : ""}? This will also remove associated call logs and tasks.`)) return;

    setDeleting(true);
    try {
      const response = await fetch(`${API}/leads/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_ids: Array.from(selectedLeads) })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.count} lead${data.count !== 1 ? "s" : ""}`);
        setSelectedLeads(new Set());
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete leads");
      }
    } catch (error) {
      toast.error("Failed to delete leads");
    } finally {
      setDeleting(false);
    }
  };

  const groupedByStatus = {
    prospect: leads.filter(l => l.status === "prospect"),
    contacted: leads.filter(l => l.status === "contacted"),
    qualified: leads.filter(l => l.status === "qualified"),
    closed_won: leads.filter(l => l.status === "closed_won"),
    closed_lost: leads.filter(l => l.status === "closed_lost")
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Leads
          </h1>
          <p className="text-slate-500 mt-1">
            {total} total leads
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-leads-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {isPlatformOwner && (
            <Button
              variant="outline"
              onClick={handleExportAllCSV}
              data-testid="export-all-leads-btn"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              title="Platform Owner: export every lead across all organizations (includes Google Places imports)"
            >
              <Download className="w-4 h-4 mr-2" />
              Export ALL (Owner)
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/import")}
            data-testid="import-leads-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Leads
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add New Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  value={newLead.business_name}
                  onChange={(e) => setNewLead({ ...newLead, business_name: e.target.value })}
                  placeholder="Enter business name"
                  data-testid="input-business-name"
                />
              </div>

              {/* Contact Person */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Primary Contact</p>
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={newLead.contact_name}
                    onChange={(e) => setNewLead({ ...newLead, contact_name: e.target.value })}
                    placeholder="e.g., John Smith"
                    data-testid="input-contact-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={newLead.contact_email}
                      onChange={(e) => setNewLead({ ...newLead, contact_email: e.target.value })}
                      placeholder="john@company.com"
                      data-testid="input-contact-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={newLead.contact_phone}
                      onChange={(e) => setNewLead({ ...newLead, contact_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="input-contact-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Secondary Contact */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Secondary Contact</p>
                <div>
                  <Label htmlFor="contact2_name">Contact Name</Label>
                  <Input
                    id="contact2_name"
                    value={newLead.contact2_name}
                    onChange={(e) => setNewLead({ ...newLead, contact2_name: e.target.value })}
                    placeholder="e.g., Jane Doe"
                    data-testid="input-contact2-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contact2_email">Contact Email</Label>
                    <Input
                      id="contact2_email"
                      type="email"
                      value={newLead.contact2_email}
                      onChange={(e) => setNewLead({ ...newLead, contact2_email: e.target.value })}
                      placeholder="jane@company.com"
                      data-testid="input-contact2-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact2_phone">Contact Phone</Label>
                    <Input
                      id="contact2_phone"
                      value={newLead.contact2_phone}
                      onChange={(e) => setNewLead({ ...newLead, contact2_phone: e.target.value })}
                      placeholder="(555) 987-6543"
                      data-testid="input-contact2-phone"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newLead.address}
                  onChange={(e) => setNewLead({ ...newLead, address: e.target.value })}
                  placeholder="Street address"
                  data-testid="input-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={newLead.city}
                    onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                    placeholder="City"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Select
                    value={newLead.state}
                    onValueChange={(value) => setNewLead({ ...newLead, state: value })}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP</Label>
                  <Input
                    id="zip_code"
                    value={newLead.zip_code}
                    onChange={(e) => setNewLead({ ...newLead, zip_code: e.target.value })}
                    placeholder="ZIP"
                    data-testid="input-zip"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                    placeholder="email@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={newLead.website}
                  onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                  placeholder="https://example.com"
                  data-testid="input-website"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={newLead.category}
                  onChange={(e) => setNewLead({ ...newLead, category: e.target.value })}
                  placeholder="e.g., Restaurant, Retail"
                  data-testid="input-category"
                />
              </div>
              <div>
                <Label htmlFor="assigned_to">Assign To</Label>
                <Select
                  value={newLead.assigned_to || "unassigned"}
                  onValueChange={(value) => setNewLead({ ...newLead, assigned_to: value === "unassigned" ? "" : value })}
                >
                  <SelectTrigger data-testid="select-assigned-to">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newLead.notes}
                  onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                  placeholder="Add any notes..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>
              <Button 
                onClick={handleCreateLead} 
                className="w-full"
                data-testid="submit-lead-btn"
              >
                Create Lead
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Selection Action Bar */}
      {canMultiSelect && selectedLeads.size > 0 && (
        <div 
          className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg"
          data-testid="selection-action-bar"
        >
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-sm font-medium">
              {selectedLeads.size} selected
            </Badge>
            <button 
              onClick={clearSelection}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              data-testid="clear-selection-btn"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkExport}
              data-testid="bulk-export-leads-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Selected
            </Button>
            {canAssign && (
              <Button 
                size="sm" 
                onClick={() => setAssignDialogOpen(true)}
                data-testid="bulk-assign-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign to Rep
              </Button>
            )}
            <Button 
              size="sm" 
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-leads-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search leads..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-leads-input"
          />
        </div>

        <div className="flex gap-1 border border-slate-200 rounded-md p-1">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded ${viewMode === "list" ? "bg-slate-100" : ""}`}
            data-testid="view-list-btn"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode("pipeline")}
            className={`p-2 rounded ${viewMode === "pipeline" ? "bg-slate-100" : ""}`}
            data-testid="view-pipeline-btn"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Advanced filter bar */}
      <LeadFilterBar
        filters={filters}
        onChange={setFilters}
        onReset={() => { setFilters(defaultFilters()); setStatusFilter("all"); }}
      />

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : viewMode === "list" ? (
        /* List View */
        <Card>
          <CardContent className="p-0">
            {leads.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No leads found</p>
                <p className="text-sm text-slate-400 mt-1">
                  Add leads manually or search for businesses
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      {canMultiSelect && (
                        <th className="py-3 px-4 w-12">
                          <Checkbox
                            checked={leads.length > 0 && selectedLeads.size === leads.length}
                            onCheckedChange={toggleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                      )}
                      <th className="text-left py-3 px-4">Business</th>
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-left py-3 px-4">Phone</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr 
                        key={lead.lead_id}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                          selectedLeads.has(lead.lead_id) ? "bg-primary/5" : ""
                        }`}
                        data-testid={`lead-row-${lead.lead_id}`}
                      >
                        {canMultiSelect && (
                          <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedLeads.has(lead.lead_id)}
                              onCheckedChange={() => toggleSelectLead(lead.lead_id)}
                              data-testid={`select-lead-${lead.lead_id}`}
                            />
                          </td>
                        )}
                        <td className="py-3 px-4" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                          <p className="font-medium text-slate-900">{lead.business_name}</p>
                          {lead.contact_name && (
                            <p className="text-xs text-blue-600">{lead.contact_name}</p>
                          )}
                          {!lead.contact_name && lead.category && (
                            <p className="text-xs text-slate-500">{lead.category}</p>
                          )}
                        </td>
                        <td className="py-3 px-4" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <MapPin className="w-3 h-3" />
                            {lead.city}, {lead.state}
                          </div>
                        </td>
                        <td className="py-3 px-4" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                          {lead.phone ? (
                            <div className="flex items-center gap-1 text-sm text-slate-600">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                          <Badge className={`${statusColors[lead.status]} text-xs`}>
                            {statusLabels[lead.status]}
                          </Badge>
                        </td>
                        <td className="py-3 px-4" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                          <span className="text-sm text-slate-600">
                            {lead.assigned_to_name || "Unassigned"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Pipeline View */
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-view">
          {Object.entries(statusLabels).map(([status, label]) => (
            <div key={status} className="pipeline-column min-w-[280px]">
              <div className="pipeline-header flex items-center justify-between">
                <span>{label}</span>
                <Badge variant="secondary" className="text-xs">
                  {groupedByStatus[status]?.length || 0}
                </Badge>
              </div>
              <div className="space-y-2">
                {groupedByStatus[status]?.map((lead) => (
                  <div
                    key={lead.lead_id}
                    className="pipeline-card"
                    onClick={() => navigate(`/leads/${lead.lead_id}`)}
                    data-testid={`pipeline-card-${lead.lead_id}`}
                  >
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {lead.business_name}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                      <MapPin className="w-3 h-3" />
                      {lead.city}, {lead.state}
                    </div>
                    {lead.phone && (
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                        <Phone className="w-3 h-3" />
                        {lead.phone}
                      </div>
                    )}
                    {lead.assigned_to_name && (
                      <p className="text-xs text-slate-400 mt-2">
                        {lead.assigned_to_name}
                      </p>
                    )}
                  </div>
                ))}
                {groupedByStatus[status]?.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">
                    No leads
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Leads</DialogTitle>
            <DialogDescription>
              Assign {selectedLeads.size} selected lead{selectedLeads.size !== 1 ? "s" : ""} to a team member
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">Select Team Member</Label>
            <Select value={assignToUserId} onValueChange={setAssignToUserId}>
              <SelectTrigger data-testid="assign-user-select">
                <SelectValue placeholder="Choose a team member" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignDialogOpen(false); setAssignToUserId(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAssign} 
              disabled={assigning || !assignToUserId}
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
